<template>
  <van-popup
    :value="value"
    class="email-change-popup"
    position="bottom"
    :close-on-click-overlay="false"
    :safe-area-inset-bottom="true"
    @input="handlePopupVisibility"
  >
    <section
      ref="dialog"
      class="email-change-sheet"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
      :aria-labelledby="titleId"
      :aria-describedby="descriptionId"
      :aria-busy="requestLoading ? 'true' : 'false'"
      :style="sheetHeightStyle"
      @keydown.esc="handleClose('escape')"
      @keydown.tab="handleFocusTrap"
      @transitionend="handleSheetHeightTransitionEnd"
    >
      <header ref="sheetHeader" class="sheet-header">
        <div>
          <h2 :id="titleId">{{ sheetTitle }}</h2>
        </div>
        <button
          type="button"
          class="icon-button"
          aria-label="关闭电邮地址设置"
          :disabled="requestLoading"
          @click="handleClose('close-button')"
        >
          <span aria-hidden="true">×</span>
        </button>
      </header>

      <div ref="sheetBody" class="sheet-body">
        <template v-if="step === STEPS.FORM">
          <p :id="descriptionId" class="description">
            {{ formDescription }}
          </p>

          <div v-if="activeEmailMasked" class="current-email-card">
            <div>
              <span class="meta-label">当前电邮地址</span>
              <strong>{{ activeEmailMasked }}</strong>
            </div>
            <span class="status-badge" :class="statusBadgeClass">
              {{ statusBadgeText }}
            </span>
          </div>

          <form novalidate @submit.prevent="handleRequestVerification">
            <div class="field-group">
              <label :for="emailInputId">{{ emailFieldLabel }}</label>
              <div class="email-field-wrap">
                <input
                  :id="emailInputId"
                  ref="emailInput"
                  v-model="emailDraft"
                  class="email-input"
                  :class="{ invalid: emailError }"
                  type="email"
                  inputmode="email"
                  autocomplete="email"
                  autocapitalize="none"
                  spellcheck="false"
                  maxlength="254"
                  :placeholder="emailPlaceholder"
                  :aria-invalid="emailError ? 'true' : 'false'"
                  :aria-describedby="emailHelpId"
                  :aria-expanded="showSuggestions ? 'true' : 'false'"
                  :aria-controls="suggestionsId"
                  @input="handleEmailInput"
                  @focus="handleEmailFocus"
                  @blur="handleEmailBlur"
                >
                <ul
                  v-if="showSuggestions"
                  :id="suggestionsId"
                  class="email-suggestions"
                  role="listbox"
                  aria-label="电邮地址域名建议"
                >
                  <li
                    v-for="suggestion in emailSuggestions"
                    :key="suggestion"
                    role="presentation"
                  >
                    <button
                      type="button"
                      role="option"
                      :aria-label="'使用电邮地址 ' + suggestion"
                      @mousedown.prevent
                      @click="selectEmailSuggestion(suggestion)"
                    >
                      {{ suggestion }}
                    </button>
                  </li>
                </ul>
              </div>
              <div :id="emailHelpId" class="field-message">
                <p v-if="emailError" class="error-text" role="alert">
                  {{ emailError }}
                </p>
                <p v-else class="helper-text">
                  验证成功前，现有电邮地址和通知设置不会改变。
                </p>
              </div>
            </div>

            <p v-if="requestError" class="request-error" role="alert">
              {{ requestError }}
            </p>

            <div class="form-actions">
              <button
                type="submit"
                class="button button-primary"
                :disabled="requestLoading || !canSubmitEmail"
              >
                <span v-if="requestLoading" class="spinner" aria-hidden="true" />
                {{ requestLoading ? '提交中...' : '立即绑定' }}
              </button>
            </div>
          </form>
        </template>

        <template v-else>
          <div class="sent-state">
            <div class="sent-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" focusable="false">
                <path d="M3.8 6.5 12 12l8.2-5.5M5 18.5h14a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2Z" />
                <path d="m8.8 11.1-5.2 5.2m16.8 0-5.2-5.2" />
              </svg>
            </div>
            <p :id="descriptionId" class="sent-heading">验证邮件已发送</p>
            <p class="sent-copy">
              我们已将验证链接发送至
              <strong>{{ sentDestination }}</strong>
            </p>
            <p class="activation-note" role="status" aria-live="polite">
              请前往电邮地址激活。
            </p>
            <p v-if="hasActiveEmail" class="safety-note">
              完成验证前，当前电邮地址会继续生效。
            </p>
          </div>

          <p
            class="request-error sent-request-error"
            :class="{ 'is-visible': requestError }"
            :role="requestError ? 'alert' : null"
            aria-live="polite"
          >
            {{ requestError }}
          </p>

          <div class="sent-actions">
            <button
              type="button"
              class="button button-verify-check"
              :disabled="requestLoading || verificationExpired"
              @click="handleCheckVerification"
            >
              <span v-if="requestLoading && requestAction === 'verify'" class="spinner" aria-hidden="true" />
              {{ requestLoading && requestAction === 'verify' ? '加载中...' : '我验证了' }}
            </button>
            <button
              type="button"
              class="button button-primary"
              :disabled="requestLoading || resendSeconds > 0"
              :aria-describedby="resendHelpId"
              @click="handleResend"
            >
              <span v-if="requestLoading && requestAction === 'resend'" class="spinner" aria-hidden="true" />
              {{ resendButtonText }}
            </button>
            <p :id="resendHelpId" class="visually-hidden" aria-live="polite">
              {{ resendAssistiveText }}
            </p>
            <button
              type="button"
              class="text-button"
              :disabled="requestLoading"
              @click="handleChangeEmail"
            >
              更换电邮地址
            </button>
          </div>
        </template>
      </div>
    </section>
  </van-popup>
</template>

<script>
import { Popup } from 'vant'
import api from '@/plugins/api'
import { isValidAccountEmail } from '@/utils/emailValidation'

const STEPS = Object.freeze({
  FORM: 'FORM',
  SENT: 'SENT'
})

const EMAIL_STATUSES = Object.freeze({
  MISSING: 'MISSING',
  UNVERIFIED: 'UNVERIFIED',
  VERIFIED: 'VERIFIED'
})

export default {
  name: 'EmailChangeSheet',
  components: {
    [Popup.name]: Popup
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    gts2CustomerId: {
      type: [String, Number],
      default: ''
    },
    emailState: {
      type: Object,
      default: () => ({
        emailStatus: EMAIL_STATUSES.MISSING,
        originalEmail: '',
        activeEmail: null,
        activeEmailMasked: '',
        pendingChange: null,
        expiredChange: null
      })
    }
  },
  data() {
    return {
      STEPS,
      step: STEPS.FORM,
      emailDraft: '',
      emailError: '',
      emailTouched: false,
      suggestionsVisible: false,
      requestLoading: false,
      requestAction: '',
      requestError: '',
      verificationExpired: false,
      pendingChange: null,
      isReplacingPendingEmail: false,
      resendSeconds: 0,
      clockTimer: null,
      sheetHeight: '',
      sheetHeightTimer: null,
      focusAfterHeightTransition: false,
      previousFocusedElement: null,
      suggestedEmailDomains: [
        'qq.com',
        '163.com',
        '126.com',
        'sina.com',
        'outlook.com',
        'yahoo.com'
      ]
    }
  },
  computed: {
    titleId() {
      return `email-change-title-${this._uid}`
    },
    descriptionId() {
      return `email-change-description-${this._uid}`
    },
    emailInputId() {
      return `email-change-input-${this._uid}`
    },
    emailHelpId() {
      return `email-change-help-${this._uid}`
    },
    suggestionsId() {
      return `email-change-suggestions-${this._uid}`
    },
    resendHelpId() {
      return `email-change-resend-help-${this._uid}`
    },
    sheetHeightStyle() {
      return this.sheetHeight ? { height: this.sheetHeight } : null
    },
    normalizedEmailStatus() {
      const status = this.emailState && this.emailState.emailStatus
      return Object.prototype.hasOwnProperty.call(EMAIL_STATUSES, status)
        ? status
        : EMAIL_STATUSES.MISSING
    },
    activeEmail() {
      return this.emailState && typeof this.emailState.activeEmail === 'string'
        ? this.emailState.activeEmail.trim()
        : ''
    },
    originalEmail() {
      if (this.emailState && typeof this.emailState.originalEmail === 'string') {
        return this.emailState.originalEmail.trim()
      }
      return this.activeEmail
    },
    activeEmailMasked() {
      if (this.emailState && this.emailState.activeEmailMasked) {
        return this.emailState.activeEmailMasked
      }
      return this.maskEmail(this.activeEmail)
    },
    hasActiveEmail() {
      return Boolean(this.activeEmail)
    },
    flowMode() {
      if (this.normalizedEmailStatus === EMAIL_STATUSES.UNVERIFIED) return 'VERIFY'
      if (this.normalizedEmailStatus === EMAIL_STATUSES.VERIFIED) return 'EDIT'
      return 'ADD'
    },
    sheetTitle() {
      if (this.step === STEPS.SENT) return '验证电邮地址'
      if (this.flowMode === 'EDIT') return '修改电邮地址'
      if (this.flowMode === 'VERIFY') return '验证电邮地址'
      return '添加电邮地址'
    },
    formDescription() {
      if (this.flowMode === 'EDIT') {
        return '输入新的常用电邮地址，我们会发送一封验证邮件确认归属。'
      }
      if (this.flowMode === 'VERIFY') {
        return '验证电邮地址后，可更可靠地接收账户和安全通知。'
      }
      return '添加并验证常用电邮地址，用于接收账户和安全通知。'
    },
    emailFieldLabel() {
      return this.flowMode === 'EDIT' ? '新电邮地址' : '电邮地址'
    },
    emailPlaceholder() {
      return this.flowMode === 'EDIT' ? '请输入新电邮地址' : '请输入电邮地址'
    },
    statusBadgeText() {
      if (this.normalizedEmailStatus === EMAIL_STATUSES.VERIFIED) return '已绑定'
      return '未验证'
    },
    statusBadgeClass() {
      return {
        verified: this.normalizedEmailStatus === EMAIL_STATUSES.VERIFIED
      }
    },
    emailSuggestions() {
      const value = this.emailDraft.trim()
      const atIndex = value.indexOf('@')
      if (atIndex <= 0 || value.indexOf('@', atIndex + 1) !== -1) return []

      const localPart = value.slice(0, atIndex)
      const typedDomain = value.slice(atIndex + 1).toLowerCase()
      if (typedDomain.includes('.')) return []

      return this.suggestedEmailDomains
        .filter(domain => domain.startsWith(typedDomain))
        .slice(0, 5)
        .map(domain => `${localPart}@${domain}`)
    },
    showSuggestions() {
      return this.suggestionsVisible && this.emailSuggestions.length > 0
    },
    canSubmitEmail() {
      return this.getEmailValidationMessage(this.emailDraft) === ''
    },
    sentDestination() {
      if (!this.pendingChange) return this.maskEmail(this.emailDraft)
      return this.pendingChange.maskedEmail || this.maskEmail(this.pendingChange.email)
    },
    resendButtonText() {
      if (this.requestLoading && this.requestAction === 'resend') return '重新发送中…'
      if (this.resendSeconds > 0) return `${this.resendSeconds} 秒后可重发`
      return '重新发送'
    },
    resendAssistiveText() {
      if (this.resendSeconds > 0) {
        return `还需等待 ${this.resendSeconds} 秒才能重新发送验证邮件。`
      }
      return '现在可以重新发送验证邮件。'
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(isOpen) {
        if (isOpen) {
          this.openSheet()
        } else {
          this.stopClock()
          this.resetSheetHeightTransition()
          this.restoreFocus()
        }
      }
    },
    emailState: {
      deep: true,
      handler(nextState) {
        if (
          !this.value ||
          !nextState ||
          !nextState.pendingChange ||
          this.isReplacingPendingEmail
        ) return
        this.applyPendingChange(nextState.pendingChange)
      }
    }
  },
  beforeDestroy() {
    this.stopClock()
    this.resetSheetHeightTransition()
  },
  methods: {
    openSheet() {
      this.resetSheetHeightTransition()
      if (typeof document !== 'undefined') {
        this.previousFocusedElement = document.activeElement
      }
      this.requestError = ''
      this.verificationExpired = false
      this.emailError = ''
      this.emailTouched = false
      this.suggestionsVisible = false
      this.isReplacingPendingEmail = false

      const expired = this.emailState && this.emailState.expiredChange
      const pending = this.emailState && this.emailState.pendingChange
      if (expired) {
        this.applyPendingChange(expired)
        this.markVerificationExpired()
        this.focusDialog()
      } else if (pending) {
        this.applyPendingChange(pending)
        this.focusDialog()
      } else if (
        this.normalizedEmailStatus === EMAIL_STATUSES.UNVERIFIED &&
        this.activeEmail
      ) {
        this.applyPendingChange(this.createPendingChange(this.activeEmail, false))
        this.focusDialog()
      } else {
        this.pendingChange = null
        this.step = STEPS.FORM
        this.emailDraft = this.flowMode === 'VERIFY' ? this.activeEmail : ''
        this.stopClock()
        this.focusEmailInput()
      }
    },
    handlePopupVisibility(isVisible) {
      if (!isVisible) this.handleClose('popup')
    },
    handleClose(reason) {
      if (this.requestLoading) return
      this.suggestionsVisible = false
      this.$emit('input', false)
      this.$emit('close', reason)
    },
    focusEmailInput(selectValue = false) {
      this.$nextTick(() => {
        const input = this.$refs.emailInput
        if (!input || typeof input.focus !== 'function') return
        input.focus()
        if (selectValue && typeof input.select === 'function') input.select()
      })
    },
    focusDialog() {
      this.$nextTick(() => {
        const dialog = this.$refs.dialog
        if (dialog && typeof dialog.focus === 'function') dialog.focus()
      })
    },
    handleFocusTrap(event) {
      const dialog = this.$refs.dialog
      if (!dialog) return

      const focusableElements = Array.from(dialog.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      )).filter(element => element.offsetParent !== null)
      if (!focusableElements.length) {
        event.preventDefault()
        dialog.focus()
        return
      }

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    },
    restoreFocus() {
      const element = this.previousFocusedElement
      this.previousFocusedElement = null
      this.$nextTick(() => {
        if (element && typeof element.focus === 'function' && document.contains(element)) {
          element.focus()
        }
      })
    },
    handleEmailInput() {
      this.requestError = ''
      this.suggestionsVisible = true
      if (this.emailTouched) this.validateEmail()
    },
    handleEmailFocus() {
      this.suggestionsVisible = true
    },
    handleEmailBlur() {
      this.emailTouched = true
      this.validateEmail()
      window.setTimeout(() => {
        this.suggestionsVisible = false
      }, 120)
    },
    selectEmailSuggestion(suggestion) {
      this.emailDraft = suggestion
      this.suggestionsVisible = false
      this.emailTouched = true
      this.validateEmail()
      this.focusEmailInput()
    },
    getEmailValidationMessage(rawEmail) {
      const inputValue = String(rawEmail || '')
      const email = inputValue.trim()
      if (!email) return '请输入电邮地址。'
      if (inputValue.length > 254) return '电邮地址不能超过 254 个字符。'

      if (!isValidAccountEmail(inputValue)) return '电邮地址格式不正确！'

      if (
        this.flowMode === 'EDIT' &&
        this.activeEmail &&
        email.toLowerCase() === this.activeEmail.toLowerCase()
      ) {
        return '新电邮地址不能与当前电邮地址相同。'
      }
      return ''
    },
    validateEmail() {
      this.emailError = this.getEmailValidationMessage(this.emailDraft)
      return this.emailError === ''
    },
    async handleRequestVerification() {
      this.emailTouched = true
      this.requestError = ''
      if (!this.validateEmail() || this.requestLoading) return

      const email = this.emailDraft.trim()
      this.setRequestLoading('request')
      try {
        const response = await api.sendVerifyEmail(
          this.$qs.stringify({ email })
        )
        if (!this.isSuccessfulResponse(response)) {
          this.requestError = this.getResponseErrorMessage(response)
          return
        }
        const pending = this.createPendingChange(email, true, this.originalEmail)
        this.applyPendingChange(pending, true)
        this.emitPendingState(pending)
        this.focusDialog()
      } catch (error) {
        this.requestError = this.getRequestErrorMessage(error)
      } finally {
        this.clearRequestLoading()
      }
    },
    async handleResend() {
      if (
        this.requestLoading ||
        this.resendSeconds > 0 ||
        !this.pendingChange ||
        !this.pendingChange.email
      ) return

      this.requestError = ''
      this.setRequestLoading('resend')
      try {
        const response = await api.sendVerifyEmail(
          this.$qs.stringify({ email: this.pendingChange.email })
        )
        if (!this.isSuccessfulResponse(response)) {
          this.requestError = this.getResponseErrorMessage(response)
          return
        }
        const pending = this.createPendingChange(
          this.pendingChange.email,
          true,
          this.pendingChange.originalEmail
        )
        this.applyPendingChange(pending)
        this.emitPendingState(pending)
      } catch (error) {
        this.requestError = this.getRequestErrorMessage(error)
      } finally {
        this.clearRequestLoading()
      }
    },
    handleChangeEmail() {
      if (this.requestLoading || !this.pendingChange) return

      const dialog = this.$refs.dialog
      const startHeight = dialog
        ? Math.ceil(dialog.getBoundingClientRect().height)
        : 0
      const previousEmail = this.pendingChange && this.pendingChange.email
      this.isReplacingPendingEmail = true
      this.focusAfterHeightTransition = true
      if (startHeight) this.sheetHeight = `${startHeight}px`
      this.step = STEPS.FORM
      this.requestError = ''
      this.emailError = ''
      this.emailTouched = false
      this.emailDraft = previousEmail || ''
      this.animateSheetHeightToContent(dialog, startHeight)
    },
    animateSheetHeightToContent(dialog, startHeight) {
      this.$nextTick(() => {
        if (!dialog || !startHeight) {
          this.finishSheetHeightTransition()
          return
        }

        const header = this.$refs.sheetHeader
        const body = this.$refs.sheetBody
        const naturalHeight = Math.ceil(
          (header ? header.getBoundingClientRect().height : 0) +
          (body ? body.scrollHeight : 0)
        )
        const targetHeight = Math.min(
          naturalHeight,
          Math.floor(window.innerHeight * 0.88)
        )

        if (!targetHeight || Math.abs(startHeight - targetHeight) < 1) {
          this.finishSheetHeightTransition()
          return
        }

        // Commit the starting height before transitioning to the target view.
        dialog.getBoundingClientRect()
        window.requestAnimationFrame(() => {
          if (!this.value || !this.sheetHeight) return
          this.sheetHeight = `${targetHeight}px`
        })
        this.sheetHeightTimer = window.setTimeout(
          this.finishSheetHeightTransition,
          400
        )
      })
    },
    handleSheetHeightTransitionEnd(event) {
      if (
        event.target === event.currentTarget &&
        event.propertyName === 'height'
      ) {
        this.finishSheetHeightTransition()
      }
    },
    finishSheetHeightTransition() {
      if (this.sheetHeightTimer) window.clearTimeout(this.sheetHeightTimer)
      this.sheetHeightTimer = null
      this.sheetHeight = ''

      if (this.focusAfterHeightTransition) {
        this.focusAfterHeightTransition = false
        this.focusEmailInput(true)
      }
    },
    resetSheetHeightTransition() {
      if (this.sheetHeightTimer) window.clearTimeout(this.sheetHeightTimer)
      this.sheetHeightTimer = null
      this.sheetHeight = ''
      this.focusAfterHeightTransition = false
    },
    async handleCheckVerification() {
      if (this.requestLoading || !this.pendingChange) return

      let verificationComplete = false
      this.requestError = ''
      this.setRequestLoading('verify')
      try {
        const response = await api.getAccTradeInfo(this.gts2CustomerId)
        if (!this.isSuccessfulResponse(response)) {
          this.requestError = this.getResponseErrorMessage(response)
          return
        }

        const accountInfo = response.data.data && response.data.data.accountInfo
        if (!accountInfo) {
          this.requestError = '暂时无法取得最新电邮地址状态，请稍后再试。'
          return
        }

        const verificationState = this.resolveVerificationState(accountInfo)
        this.$emit('account-info-refreshed', accountInfo)
        if (verificationState === 'EXPIRED') {
          this.markVerificationExpired()
          return
        }
        if (verificationState !== 'VERIFIED') {
          this.requestError = '验证尚未完成，请检查您的邮箱并完成验证。'
          return
        }

        verificationComplete = true
      } catch (error) {
        this.requestError = this.getRequestErrorMessage(error)
      } finally {
        this.clearRequestLoading()
      }

      if (!verificationComplete) return
      this.pendingChange = null
      this.stopClock()
      this.handleClose('verification-complete')
    },
    createPendingChange(email, useResendCooldown, originalEmail = this.originalEmail) {
      const now = Date.now()
      return {
        email,
        originalEmail,
        maskedEmail: this.maskEmail(email),
        resendAvailableAt: useResendCooldown ? now + (60 * 1000) : now
      }
    },
    applyPendingChange(pending, animateHeight = false) {
      let dialog = null
      let startHeight = 0
      if (animateHeight) {
        this.resetSheetHeightTransition()
        dialog = this.$refs.dialog
        startHeight = dialog
          ? Math.ceil(dialog.getBoundingClientRect().height)
          : 0
        if (startHeight) this.sheetHeight = `${startHeight}px`
      }

      const now = Date.now()
      const normalizedPending = Object.assign({}, pending, {
        resendAvailableAt: this.normalizeTimestamp(pending.resendAvailableAt) || now
      })
      this.pendingChange = normalizedPending
      this.verificationExpired = false
      this.isReplacingPendingEmail = false
      this.emailDraft = normalizedPending.email || this.emailDraft
      this.step = STEPS.SENT
      this.startClock()
      if (animateHeight) {
        this.animateSheetHeightToContent(dialog, startHeight)
      }
    },
    emitPendingState(pending) {
      this.$emit('state-changed', Object.assign({}, this.emailState, {
        pendingChange: pending,
        expiredChange: null
      }))
    },
    resolveVerificationState(accountInfo) {
      const currentEmail = this.normalizeEmail(accountInfo && accountInfo.email)
      const newEmail = this.normalizeEmail(accountInfo && accountInfo.emailNew)
      const pendingEmail = this.normalizeEmail(this.pendingChange && this.pendingChange.email)
      const originalEmail = this.normalizeEmail(
        this.pendingChange && this.pendingChange.originalEmail
      )
      const emailStatus = Number(accountInfo && accountInfo.emailStatus)
      const hasNewEmail = !this.isMissingEmail(newEmail)

      if (
        emailStatus === 0 &&
        hasNewEmail &&
        currentEmail === originalEmail &&
        newEmail === pendingEmail
      ) {
        return 'PENDING'
      }
      if (
        emailStatus === 1 &&
        !hasNewEmail &&
        currentEmail === pendingEmail
      ) {
        return 'VERIFIED'
      }
      if (
        emailStatus === 1 &&
        !hasNewEmail &&
        currentEmail === originalEmail &&
        currentEmail !== pendingEmail
      ) {
        return 'EXPIRED'
      }
      return 'INCOMPLETE'
    },
    markVerificationExpired() {
      this.verificationExpired = true
      this.requestError = '验证链接已过期，请点击“重新发送”获取新验证链接。'
      this.stopClock()
      this.resendSeconds = 0
      this.pendingChange = Object.assign({}, this.pendingChange, {
        resendAvailableAt: Date.now()
      })
    },
    normalizeEmail(email) {
      return String(email || '').trim().toLowerCase()
    },
    isMissingEmail(email) {
      const normalized = this.normalizeEmail(email)
      return normalized === '' ||
        normalized === 'systemfxservice@outlook.com' ||
        normalized === 'realemail'
    },
    normalizeTimestamp(value) {
      if (!value) return 0
      if (typeof value === 'number') return value < 1000000000000 ? value * 1000 : value
      const numericValue = Number(value)
      if (!Number.isNaN(numericValue) && numericValue > 0) {
        return numericValue < 1000000000000 ? numericValue * 1000 : numericValue
      }
      const parsedValue = Date.parse(value)
      return Number.isNaN(parsedValue) ? 0 : parsedValue
    },
    startClock() {
      this.stopClock()
      this.updateTemporalState()
      this.clockTimer = window.setInterval(this.updateTemporalState, 1000)
    },
    stopClock() {
      if (this.clockTimer) window.clearInterval(this.clockTimer)
      this.clockTimer = null
    },
    updateTemporalState() {
      if (!this.pendingChange) {
        this.resendSeconds = 0
        return
      }
      const now = Date.now()
      this.resendSeconds = Math.max(
        0,
        Math.ceil((this.pendingChange.resendAvailableAt - now) / 1000)
      )
    },
    setRequestLoading(action) {
      this.requestLoading = true
      this.requestAction = action
    },
    clearRequestLoading() {
      this.requestLoading = false
      this.requestAction = ''
    },
    isSuccessfulResponse(response) {
      const data = response && response.data
      if (!data) return false
      if (data.code !== undefined) return Number(data.code) === 200
      if (data.status !== undefined) return data.status === 'SUCCESS'
      return response.status >= 200 && response.status < 300
    },
    getResponseErrorMessage(response, fallback = '操作未完成，请稍后重试。') {
      const data = response && response.data
      return data && (data.code_desc || data.ch_msg || data.message) ||
        fallback
    },
    getRequestErrorMessage(error) {
      return this.getResponseErrorMessage(
        error && error.response,
        '网络连接异常，请检查网络后重试。'
      )
    },
    maskEmail(email) {
      const value = String(email || '')
      const parts = value.split('@')
      if (parts.length !== 2 || !parts[0] || !parts[1]) return value
      const localPart = parts[0]
      let maskedLocal = `${localPart.charAt(0)}***`
      if (localPart.length > 2) maskedLocal += localPart.charAt(localPart.length - 1)
      return `${maskedLocal}@${parts[1]}`
    }
  }
}
</script>

<style scoped>
.email-change-popup {
  width: 100%;
  max-height: 88vh;
  overflow: hidden;
  border-radius: .48rem .48rem 0 0;
  background: #fff;
}

.email-change-sheet {
  color: #1f2937;
  background: #fff;
  max-height: 88vh;
  overflow-y: auto;
  overscroll-behavior: contain;
  transition: height .28s cubic-bezier(.22, 1, .36, 1);
  -webkit-overflow-scrolling: touch;
}

.sheet-header {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 1.12rem;
  padding: .12rem .3rem;
  border-bottom: .02rem solid #edf0f2;
}

.sheet-header h2 {
  margin: 0;
  font-size: .38rem;
  line-height: 1.35;
  color: #111827;
}

.icon-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: .88rem;
  height: .88rem;
  margin-right: -.18rem;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #4b5563;
  font-size: .5rem;
  line-height: 1;
}

.sheet-body {
  padding: .34rem .3rem calc(.36rem + env(safe-area-inset-bottom));
}

.description {
  margin: 0 0 .3rem;
  color: #6b7280;
  font-size: .27rem;
  line-height: 1.55;
}

.current-email-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: .2rem;
  padding: .24rem;
  margin-bottom: .34rem;
  border: .02rem solid #e5e7eb;
  border-radius: .14rem;
  background: #f9fafb;
}

.current-email-card strong,
.meta-label {
  display: block;
}

.current-email-card strong {
  margin-top: .06rem;
  font-size: .28rem;
  line-height: 1.4;
  word-break: break-all;
}

.meta-label {
  color: #6b7280;
  font-size: .22rem;
}

.status-badge {
  flex: 0 0 auto;
  padding: .08rem .16rem;
  border-radius: .2rem;
  background: #fef3c7;
  color: #92400e;
  font-size: .22rem;
  font-weight: 600;
}

.status-badge.verified {
  background: #dcfce7;
  color: #166534;
}

.field-group label {
  display: block;
  margin-bottom: .12rem;
  color: #374151;
  font-size: .28rem;
  font-weight: 600;
  line-height: 1.2;
}

.email-field-wrap {
  position: relative;
}

.email-input {
  box-sizing: border-box;
  display: block;
  width: 100%;
  height: .92rem;
  padding: 0 .28rem;
  border: .02rem solid #bfc4cb;
  border-radius: .12rem;
  background: #fff;
  color: #111827;
  font-size: .3rem;
  line-height: .92rem;
  -webkit-appearance: none;
}

.email-input::placeholder {
  color: #9ca3af;
}

.email-input:focus {
  border-color: #0a95ff;
  box-shadow: 0 0 0 .05rem rgba(10, 149, 255, .16);
}

.email-input.invalid {
  border-color: #d92d20;
}

.email-suggestions {
  position: absolute;
  z-index: 3;
  top: calc(100% + .08rem);
  right: 0;
  left: 0;
  max-height: 3.6rem;
  overflow-y: auto;
  margin: 0;
  padding: .08rem 0;
  border: .02rem solid #e5e7eb;
  border-radius: .12rem;
  background: #fff;
  box-shadow: 0 .14rem .34rem rgba(15, 23, 42, .14);
  list-style: none;
}

.email-suggestions li + li {
  border-top: .02rem solid #f1f3f5;
}

.email-suggestions button {
  width: 100%;
  min-height: .76rem;
  padding: .14rem .26rem;
  border: 0;
  background: transparent;
  color: #1f2937;
  font-size: .28rem;
  text-align: left;
  word-break: break-all;
}

.email-suggestions button:active,
.email-suggestions button:focus {
  background: #f0f8ff;
}

.field-message {
  min-height: .6rem;
  padding-top: .12rem;
}

.helper-text,
.error-text,
.request-error {
  margin: 0;
  font-size: .24rem;
  line-height: 1.5;
}

.helper-text {
  color: #6b7280;
}

.error-text,
.request-error {
  color: #c62828;
}

.request-error {
  padding: .18rem .22rem;
  margin: .08rem 0 .2rem;
  border-radius: .1rem;
  background: #fff1f0;
}

.sent-request-error {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: .72rem;
  padding: .12rem .16rem;
  margin: .18rem 0;
  visibility: hidden;
  font-size: .22rem;
  line-height: 1.4;
  white-space: nowrap;
  text-align: center;
}

.sent-request-error.is-visible {
  visibility: visible;
}

.form-actions,
.sent-actions {
  display: grid;
  gap: .18rem;
  margin-top: .26rem;
}

.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  min-height: .9rem;
  padding: .16rem .22rem;
  border: .02rem solid transparent;
  border-radius: .12rem;
  font-size: .3rem;
  font-weight: 600;
  line-height: 1.3;
}

.button-primary {
  background: #0a95ff;
  color: #fff;
}

.button-verify-check {
  background: #16a34a;
  color: #fff;
}

.button:disabled,
.text-button:disabled,
.icon-button:disabled {
  cursor: not-allowed;
  opacity: .52;
}

.button:focus,
.text-button:focus,
.icon-button:focus {
  outline: .04rem solid rgba(10, 149, 255, .42);
  outline-offset: .04rem;
}

.spinner {
  width: .28rem;
  height: .28rem;
  margin-right: .12rem;
  border: .04rem solid rgba(255, 255, 255, .45);
  border-top-color: #fff;
  border-radius: 50%;
  animation: email-spinner .75s linear infinite;
}

.sent-state {
  text-align: center;
}

.sent-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.16rem;
  height: 1.16rem;
  margin: .06rem auto .24rem;
  border-radius: 50%;
  background: #e9f6ff;
  color: #0a95ff;
}

.sent-icon svg {
  width: .62rem;
  height: .62rem;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.7;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.sent-heading {
  margin: 0;
  color: #111827;
  font-size: .34rem;
  font-weight: 700;
}

.sent-copy {
  margin: .14rem auto 0;
  color: #5f6875;
  font-size: .27rem;
  line-height: 1.55;
}

.sent-copy strong {
  display: block;
  margin-top: .06rem;
  color: #1f2937;
  font-size: .3rem;
  word-break: break-all;
}

.activation-note {
  margin: .08rem 0 0;
  color: #6b7280;
  font-size: .27rem;
  line-height: 1.5;
}

.safety-note {
  margin: .18rem 0 0;
  color: #6b7280;
  font-size: .23rem;
}

.sent-actions {
  grid-template-columns: 1fr;
  margin-top: 0;
}

.text-button {
  min-height: .72rem;
  border: 0;
  background: transparent;
  color: #087fda;
  font-size: .27rem;
}

.visually-hidden {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

@keyframes email-spinner {
  to { transform: rotate(360deg); }
}

@media (min-width: 600px) {
  .email-change-popup {
    right: 50%;
    left: auto;
    width: 7.5rem;
    transform: translate3d(50%, 0, 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .spinner {
    animation-duration: 1.5s;
  }
}
</style>
